/**
 * SchemaTreeDropTarget — wraps children with HTML5 drop-target props. In RO
 * mode (`enabled=false`) the drop handler is a no-op; in VQE-Edit
 * (`enabled=true`) the consumer's `onDrop` callback receives the raw
 * `DataTransfer` so it can interpret whatever payload the drag source set.
 *
 * The `data-drop-enabled` attribute (`"false"` in RO) drives the tooltip copy
 * and lets E2E specs filter for the disabled state.
 *
 * Drop events are unconditionally cancelled (preventDefault on dragOver /
 * drop) so the browser's default "can't drop" cursor surfaces in RO and
 * the schema-tree drag operation completes without a fall-through to other
 * handlers.
 *
 * Drag-data convention: see `dragSchemaPayload.ts` for the MIME constant
 * (`VQE_DRAG_TABLE_MIME`) and the `parseVqeDragTablePayload` helper that
 * recovers a structured relation reference from a drop event.
 */

import { useState, type ReactNode } from 'react';

import { vqeCopy } from '../../ux/vqeCopy';
import { VqeTooltip } from './VqeTooltip';

export interface SchemaTreeDropTargetProps {
  readonly testId: string;
  readonly enabled?: boolean;
  readonly tooltip?: string;
  readonly onDrop?: (data: DataTransfer) => void;
  readonly className?: string;
  readonly children: ReactNode;
}

const DEFAULT_TOOLTIP_DISABLED = vqeCopy.disabled.dragSchemaTreeUnavailable;
const DEFAULT_TOOLTIP_ENABLED = 'Drop a schema-tree item to add it';

export function SchemaTreeDropTarget({
  testId,
  enabled = false,
  tooltip,
  onDrop,
  className,
  children,
}: SchemaTreeDropTargetProps) {
  const [isOver, setIsOver] = useState(false);
  const resolvedTooltip = tooltip ?? (enabled ? DEFAULT_TOOLTIP_ENABLED : DEFAULT_TOOLTIP_DISABLED);
  return (
    // Content-sized (not `block`): in the FROM row the actions follow the
    // tables on the same line when they fit.
    <VqeTooltip content={resolvedTooltip} selfProvider shrink>
      <div
        data-testid={testId}
        data-drop-enabled={enabled ? 'true' : 'false'}
        data-drop-over={isOver ? 'true' : 'false'}
        className={className}
        onDragOver={(event) => {
          event.preventDefault();
          if (enabled) setIsOver(true);
        }}
        onDragEnter={(event) => {
          event.preventDefault();
          if (enabled) setIsOver(true);
        }}
        onDragLeave={() => {
          if (enabled) setIsOver(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setIsOver(false);
          if (!enabled) return;
          onDrop?.(event.dataTransfer);
        }}
      >
        {children}
      </div>
    </VqeTooltip>
  );
}
